/**
 * Brisk Teaching — AI tools that work inside the sites teachers already use
 *
 * Its first web-accessible-resource group exposes assets/**, locales/* and the
 * translation JSON to file, http and https origins with no `use_dynamic_url` — so
 * every bundled asset is fetchable from any page at a fixed URL. (The extension's other
 * WAR entries are per-site scripts scoped to ChatGPT, Google Docs, Nearpod and YouTube,
 * and don't matter here.)
 *
 * The vendor-named brand images are the cleanest signal: they load through `probeImage`
 * with no CORS grant, so they survive a restrictive page CSP and are conclusive — the
 * file is only served when Brisk is installed.
 *
 * Manifest verified against version 1.0.401.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'pcblbflgdkdfdjpjifeppkljdnaekohj';

export const briskTeaching: ExtensionDefinition = {
  id: ID,
  name: 'Brisk Teaching - AI that Works Where Teachers Work',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 1_000_000,
  notes:
    'assets/** exposed to every http(s) origin with no dynamic URL. The vendor-named ' +
    'brisk_boost brand images are conclusive and load without a CORS grant.',
  signals: [
    {
      id: 'war-brisk-boost-logo',
      kind: 'web-accessible-resource',
      description:
        'Load assets/brisk_boost_logo_large.png via <img>. A vendor-named brand image ' +
        'exposed to every origin; needs no CORS grant, so it works under a strict CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/brisk_boost_logo_large.png'),
    },
    {
      id: 'war-boost-writing-icons',
      kind: 'web-accessible-resource',
      description:
        'Load assets/boost_writing_modal_icons.png via <img> — a second vendor-named ' +
        'image, corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/boost_writing_modal_icons.png'),
    },
    {
      id: 'war-translation-de',
      kind: 'web-accessible-resource',
      description:
        'Fetch translations/de-DE.json — a static translation bundle exposed to every ' +
        'origin, independent of the image paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'translations/de-DE.json'),
    },
  ],
};
